import { useCallback, useMemo } from 'react'
import {
  DEFAULT_FILTER_STATE,
  DEFAULT_QUERIES_STATE,
  DEFAULT_QUERY_COLLECTION_STATE,
  useSheetStore,
} from '../useSheetStore'

// Type utility to omit the first parameter of a function type
type OmitFirstParameter<T extends (arg1: any, ...args: any[]) => any> =
  T extends (arg1: any, ...args: infer P) => any ? P : never

/**
 * Hook for managing filters for a specific sheet
 *
 * @param sheetId - The ID of the sheet to manage filters for. If undefined, returns default state.
 * @returns Object with filter state and functions to manipulate it
 */
export const useFilterSheet = (sheetId?: string) => {
  // Initialize the sheet if an ID is provided
  if (sheetId) {
    useSheetStore.getState().initSheet(sheetId)
  }

  // Get filters using the pure selector
  const filters = useSheetStore(
    useCallback(
      (state) => (sheetId ? state.getFilters(sheetId) : DEFAULT_FILTER_STATE),
      [sheetId]
    )
  )

  // Get query state using the pure selector
  const queryState = useSheetStore(
    useCallback(
      (state) => (sheetId ? state.getQueries(sheetId) : DEFAULT_QUERIES_STATE),
      [sheetId]
    )
  )

  const queryCollectionState = useSheetStore(
    useCallback(
      (state) =>
        sheetId
          ? state.getQueryCollectionState(sheetId)
          : DEFAULT_QUERY_COLLECTION_STATE,
      [sheetId]
    )
  )

  const {
    setFilter,
    setFilterField,
    setSearchValue,
    setSearchField,
    setQ,
    setSortDirection,
    setSortField,
    updateFilters,
    resetFilters,
    setActiveQuery,
    setActiveView,
    setActiveQueryCollection,
    setQueryCollectionState,
    createQueryCollection,
    removeQueryCollection,
    clearQueryCollections,
    addQueryCollectionQuery,
    updateQueryCollectionQuery,
    removeQueryCollectionQuery,
    updateQueryCollection,
  } = useSheetStore()

  // Guard function to ensure sheetId exists before executing operations
  const sheetGuard = useCallback(
    <T extends (sheetId: string, ...args: any[]) => ReturnType<T>>(fn: T) =>
      (...args: OmitFirstParameter<T>) => {
        if (sheetId) return fn(sheetId, ...args)
      },
    [sheetId]
  )

  // Convenience function for updating a single filter
  const setFilterForSheet = useCallback(sheetGuard(setFilter), [
    sheetGuard,
    setFilter,
  ])

  // Convenience function for updating a filter field
  const setFilterFieldForSheet = useCallback(sheetGuard(setFilterField), [
    sheetGuard,
    setFilterField,
  ])

  // Convenience function for updating search value
  const setSearchValueForSheet = useCallback(sheetGuard(setSearchValue), [
    sheetGuard,
    setSearchValue,
  ])

  // Convenience function for updating search field
  const setSearchFieldForSheet = useCallback(sheetGuard(setSearchField), [
    sheetGuard,
    setSearchField,
  ])

  // Convenience function for updating query
  const setQForSheet = useCallback(sheetGuard(setQ), [sheetGuard, setQ])

  // Convenience function for updating sort direction
  const setSortDirectionForSheet = useCallback(sheetGuard(setSortDirection), [
    sheetGuard,
    setSortDirection,
  ])

  // Convenience function for updating sort field
  const setSortFieldForSheet = useCallback(sheetGuard(setSortField), [
    sheetGuard,
    setSortField,
  ])

  // Convenience function for updating multiple filters
  const updateFiltersForSheet = useCallback(sheetGuard(updateFilters), [
    sheetGuard,
    updateFilters,
  ])

  // Convenience function for resetting filters
  const resetFiltersForSheet = useCallback(sheetGuard(resetFilters), [
    sheetGuard,
    resetFilters,
  ])

  // Convenience functions for query management
  const setActiveQueryForSheet = useCallback(sheetGuard(setActiveQuery), [
    sheetGuard,
    setActiveQuery,
  ])

  // Convenience function for setting active view
  const setActiveViewForSheet = useCallback(sheetGuard(setActiveView), [
    sheetGuard,
    setActiveView,
  ])

  const setQueryCollectionStateForSheet = useCallback(
    sheetGuard(setQueryCollectionState),
    [sheetGuard, setQueryCollectionState]
  )

  const createCollectionForSheet = useCallback(
    sheetGuard(createQueryCollection),
    [sheetGuard, createQueryCollection]
  )

  const removeCollectionForSheet = useCallback(
    sheetGuard(removeQueryCollection),
    [sheetGuard, removeQueryCollection]
  )

  const clearCollectionsForSheet = useCallback(
    sheetGuard(clearQueryCollections),
    [sheetGuard, clearQueryCollections]
  )

  const addCollectionQueryForSheet = useCallback(
    sheetGuard(addQueryCollectionQuery),
    [sheetGuard, addQueryCollectionQuery]
  )

  const updateCollectionQueryForSheet = useCallback(
    sheetGuard(updateQueryCollectionQuery),
    [sheetGuard, updateQueryCollectionQuery]
  )

  const removeCollectionQueryForSheet = useCallback(
    sheetGuard(removeQueryCollectionQuery),
    [sheetGuard, removeQueryCollectionQuery]
  )

  const updateCollectionForSheet = useCallback(
    sheetGuard(updateQueryCollection),
    [sheetGuard, updateQueryCollection]
  )

  const setActiveQueryCollectionForSheet = useCallback(
    sheetGuard(setActiveQueryCollection),
    [sheetGuard, setActiveQueryCollection]
  )

  // Check if any filters are applied
  const hasFilters = useMemo(() => {
    return !!(
      filters.filter ||
      filters.searchValue ||
      filters.searchField ||
      filters.filterField ||
      filters.q ||
      Object.keys(queryCollectionState.queryCollections).length > 0
    )
  }, [filters, queryCollectionState.queryCollections])

  return {
    // Current filter state
    filters,
    activeQueryId: queryState.activeQueryId,
    activeViewId: queryState.activeViewId,
    queryCollections: queryCollectionState.queryCollections,
    activeQueryCollectionId: queryCollectionState.activeQueryCollectionId,
    // Filter state checks
    hasFilters,

    // Actions to update filters
    setFilter: setFilterForSheet,
    setFilterField: setFilterFieldForSheet,
    setSearchValue: setSearchValueForSheet,
    setSearchField: setSearchFieldForSheet,
    setQ: setQForSheet,
    setSortDirection: setSortDirectionForSheet,
    setSortField: setSortFieldForSheet,
    updateFilters: updateFiltersForSheet,
    resetFilters: resetFiltersForSheet,

    // Query management
    setActiveQuery: setActiveQueryForSheet,
    setActiveView: setActiveViewForSheet,

    // Query collection management
    setQueryCollectionState: setQueryCollectionStateForSheet,
    setActiveQueryCollection: setActiveQueryCollectionForSheet,
    createCollection: createCollectionForSheet,
    removeCollection: removeCollectionForSheet,
    clearCollections: clearCollectionsForSheet,
    addCollectionQuery: addCollectionQueryForSheet,
    updateCollectionQuery: updateCollectionQueryForSheet,
    removeCollectionQuery: removeCollectionQueryForSheet,
    updateCollection: updateCollectionForSheet,
  }
}
